import { EntityKindHandler, type SplitEntities, type EntityMenuContext, type AddPickerEntry, type ToolbarAction, type ToolbarActionContext } from '../EntityKindHandler';
import type { CanvasEntity, Rect } from '../../entityTypes';
import { GROUP_PADDING, COLLAPSED_GROUP_WIDTH, COLLAPSED_GROUP_HEIGHT, getGroupData } from '../../entityTypes';
import type { ContextMenuItem } from '../../../components/ContextMenu';
import { Edit2, FileText, ChevronRight, ChevronDown, Layers, Unlink, Boxes, Eye, EyeOff } from 'lucide-react';
import { buildColorGridSubmenu, buildBorderColorSubmenu } from '../../lib/contextMenuHelpers';
import { buildLayerSubmenuItems } from '../../hooks/useLayerSubmenu';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { BORDER_COLOR_DEFAULTS, BORDER_WIDTH_OPTIONS } from '../../lib/edgeStyleConstants';
import i18n from '../../../i18n';

const t = (key: string) => i18n.t(key as any) as string;

const GROUP_BG_COLOR_OPTIONS = [
  { id: 'blue', label: t('canvasEntity.colorBlue'), color: 'rgba(147, 197, 253, 0.2)' },
  { id: 'green', label: t('canvasEntity.colorGreen'), color: 'rgba(134, 239, 172, 0.2)' },
  { id: 'yellow', label: t('canvasEntity.colorYellow'), color: 'rgba(253, 224, 71, 0.2)' },
  { id: 'red', label: t('canvasEntity.colorRed'), color: 'rgba(252, 165, 165, 0.2)' },
  { id: 'purple', label: t('canvasEntity.colorPurple'), color: 'rgba(196, 181, 253, 0.2)' },
];

const DISPLAY_MODE_OPTIONS = [
  { id: 'container', label: t('canvasEntity.modeContainer') },
  { id: 'invisible', label: t('canvasEntity.modeInvisible') },
];

export class GroupHandler extends EntityKindHandler {
  readonly kind = 'group';
  readonly dataAttribute = 'data-canvas-group';
  // Component stays null — groups are rendered with dedicated logic (group/bounds props)
  canSnap = false;
  canConnect = false;
  hasOwnRect = false;

  getAddPickerEntry(): AddPickerEntry {
    return { label: t('canvasEntity.group'), icon: Boxes, iconColor: '#8b5cf6', priority: 20, mode: 'instant' };
  }

  getBounds(entity: CanvasEntity, allEntities?: CanvasEntity[]): Rect {
    const gd = getGroupData(entity);
    if (gd.collapsed) {
      return {
        x: gd.collapsedX ?? entity.x,
        y: gd.collapsedY ?? entity.y,
        width: COLLAPSED_GROUP_WIDTH,
        height: COLLAPSED_GROUP_HEIGHT,
      };
    }

    if (!allEntities || gd.memberEntityIds.length === 0) {
      return { x: entity.x, y: entity.y, width: entity.width, height: entity.height };
    }

    // Compute bounds from member entities
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let hasContent = false;

    const entityMap = new Map(allEntities.map(e => [e.id, e]));

    for (const memberId of gd.memberEntityIds) {
      const member = entityMap.get(memberId);
      if (!member) continue;

      if (member.kind === 'group') {
        // Recursively get bounds of child group
        const childBounds = this.getBounds(member, allEntities);
        hasContent = true;
        minX = Math.min(minX, childBounds.x);
        minY = Math.min(minY, childBounds.y);
        maxX = Math.max(maxX, childBounds.x + childBounds.width);
        maxY = Math.max(maxY, childBounds.y + childBounds.height);
      } else {
        hasContent = true;
        minX = Math.min(minX, member.x);
        minY = Math.min(minY, member.y);
        maxX = Math.max(maxX, member.x + member.width);
        maxY = Math.max(maxY, member.y + member.height);
      }
    }

    if (!hasContent) {
      return { x: entity.x, y: entity.y, width: entity.width, height: entity.height };
    }

    const headerOffset = (gd.displayMode ?? 'invisible') === 'container' ? 24 : 0;
    return {
      x: minX - GROUP_PADDING,
      y: minY - GROUP_PADDING - headerOffset,
      width: maxX - minX + GROUP_PADDING * 2,
      height: maxY - minY + GROUP_PADDING * 2 + headerOffset,
    };
  }

  getToolbarActions(ctx: ToolbarActionContext): ToolbarAction[] {
    const { store, entity } = ctx;
    const gd = getGroupData(entity);
    const currentMode = gd.displayMode ?? 'invisible';
    const actions: ToolbarAction[] = [];

    actions.push({
      name: 'displayMode',
      label: t('canvasEntity.mode'),
      type: 'select',
      options: DISPLAY_MODE_OPTIONS,
      getValue: () => currentMode,
      apply: (v) => store.send({ type: 'group.updateData', groupId: entity.id, dataChanges: { displayMode: v as 'container' | 'invisible' } }),
    });

    if (currentMode === 'container') {
      actions.push({
        name: 'backgroundColor',
        label: t('canvasEntity.background'),
        type: 'color',
        options: GROUP_BG_COLOR_OPTIONS,
        getValue: () => gd.backgroundColor,
        apply: (v) => store.send({ type: 'group.updateData', groupId: entity.id, dataChanges: { backgroundColor: v as string } }),
      });

      actions.push({
        name: 'borderColor',
        label: t('canvasEntity.borderColor'),
        type: 'color',
        options: BORDER_COLOR_DEFAULTS.filter(c => c.color).map(c => ({ id: c.id, label: c.label, color: c.color })),
        getValue: () => gd.borderColor ?? '',
        apply: (v) => {
          const color = v as string;
          if (!color) {
            store.send({ type: 'group.updateData', groupId: entity.id, dataChanges: { borderColor: undefined, borderWidth: undefined } });
          } else {
            store.send({ type: 'group.updateData', groupId: entity.id, dataChanges: { borderColor: color } });
          }
        },
      });

      actions.push({
        name: 'borderWidth',
        label: t('canvasEntity.borderWidth'),
        type: 'select',
        options: BORDER_WIDTH_OPTIONS,
        getValue: () => gd.borderColor ? String(gd.borderWidth ?? 1) : 'none',
        apply: (v) => {
          if (v === 'none') {
            store.send({ type: 'group.updateData', groupId: entity.id, dataChanges: { borderColor: undefined, borderWidth: undefined } });
          } else {
            store.send({ type: 'group.updateData', groupId: entity.id, dataChanges: { borderWidth: Number(v) } });
          }
        },
      });
    }

    actions.push({
      name: 'collapsed',
      label: t('canvasEntity.collapse'),
      type: 'toggle',
      getValue: () => gd.collapsed,
      apply: (v) => store.send({ type: 'group.updateData', groupId: entity.id, dataChanges: { collapsed: !!v } }),
    });

    return actions;
  }

  getContextMenuItems(ctx: EntityMenuContext): ContextMenuItem[] {
    const { store, entity, layers, groups, enteredGroupId, onGroupToggleCollapse } = ctx;
    const groupId = entity.id;
    const group = groups.find(g => g.id === groupId);
    if (!group) return [];
    const items: ContextMenuItem[] = [];
    const currentMode = group.displayMode;

    // --- Pos. 1: Edit label ---
    items.push({
      id: 'edit-label',
      label: t('canvasEntity.editGroupLabel'),
      icon: Edit2,
      onClick: () => {
        const newLabel = prompt(t('canvasEntity.groupLabelPrompt'), group.label);
        if (newLabel !== null) {
          store.send({ type: 'group.updateData', groupId, dataChanges: { label: newLabel } });
        }
      },
    });

    // --- Pos. 1: Edit description ---
    items.push({
      id: 'edit-description',
      label: t('canvasEntity.editDescription'),
      icon: FileText,
      onClick: () => {
        const newDesc = prompt(t('canvasEntity.descriptionPrompt'), group.description ?? '');
        if (newDesc !== null) {
          store.send({ type: 'group.updateData', groupId, dataChanges: { description: newDesc || undefined } });
        }
      },
    });

    // --- Pos. 2: Darstellungs-Block: Toggle display mode ---
    items.push({
      id: 'toggle-display-mode',
      label: currentMode === 'container' ? t('canvasEntity.invisibleMode') : t('canvasEntity.containerMode'),
      icon: currentMode === 'container' ? EyeOff : Eye,
      separator: true,
      onClick: () => {
        const newMode = currentMode === 'container' ? 'invisible' : 'container';
        store.send({ type: 'group.updateData', groupId, dataChanges: { displayMode: newMode } });
      },
    });

    // --- Pos. 2: Color submenu (only for container mode) ---
    if (currentMode === 'container') {
      items.push(buildColorGridSubmenu({
        id: 'color-submenu',
        label: t('canvasEntity.color'),
        currentColor: group.backgroundColor,
        handlerColors: [...GROUP_BG_COLOR_OPTIONS],
        onSelectColor: (color) => store.send({ type: 'group.updateData', groupId, dataChanges: { backgroundColor: color } }),
        enableAlpha: true,
      }));

      items.push(buildBorderColorSubmenu({
        id: 'border-color-submenu',
        label: t('canvasEntity.borderColor'),
        currentColor: group.borderColor,
        onSelectColor: (color) => store.send({ type: 'group.updateData', groupId, dataChanges: { borderColor: color } }),
        onRemoveBorder: () => store.send({ type: 'group.updateData', groupId, dataChanges: { borderColor: undefined, borderWidth: undefined } }),
      }));
    }

    // --- Expand/Collapse ---
    if (onGroupToggleCollapse) {
      items.push({
        id: 'toggle-collapse',
        label: group.collapsed ? t('canvasEntity.expand') : t('canvasEntity.collapse'),
        icon: group.collapsed ? ChevronRight : ChevronDown,
        onClick: () => onGroupToggleCollapse(groupId),
      });
    }

    // --- Pos. 5: Layer submenu ---
    items.push({
      id: 'layer-submenu',
      label: 'Layer',
      icon: Layers,
      iconColor: '#4f46e5',
      separator: true,
      children: buildLayerSubmenuItems(layers, entity.layerId, (layerId) => {
        store.send({ type: 'group.setLayer', groupId, layerId });
      }),
    });

    // --- Pos. 7: Dissolve group ---
    items.push({
      id: 'dissolve',
      label: t('canvasEntity.dissolveGroup'),
      icon: Unlink,
      iconColor: '#ea580c',
      separator: true,
      onClick: () => {
        store.send({ type: 'entity.remove', entityId: groupId });
        canvasSelectionStore.send({ type: 'entity.clear' });
        if (enteredGroupId === groupId) {
          canvasSelectionStore.send({ type: 'group.exit' });
        }
      },
    });

    return items;
  }

  onRemove(entityId: string, split: SplitEntities): SplitEntities {
    // Remove the group itself, but keep members (ungroup them).
    // Then cascade: remove parent groups that became empty as a result.
    const toRemove = new Set<string>([entityId]);

    // Iteratively find groups that become empty after removing marked entities
    let prevSize = -1;
    while (toRemove.size !== prevSize) {
      prevSize = toRemove.size;
      for (const e of Object.values(split.groups)) {
        if (toRemove.has(e.id)) continue;
        const gd = getGroupData(e);
        const remaining = gd.memberEntityIds.filter((id: string) => !toRemove.has(id));
        if (remaining.length === 0 && remaining.length < gd.memberEntityIds.length) {
          toRemove.add(e.id);
        }
      }
    }

    // Filter out removed groups and clean up memberEntityIds references
    const restGroups: Record<string, CanvasEntity> = {};
    for (const [id, e] of Object.entries(split.groups)) {
      if (toRemove.has(e.id)) continue;
      const gd = getGroupData(e);
      if (gd.memberEntityIds.some((mid: string) => toRemove.has(mid))) {
        restGroups[id] = { ...e, data: { ...e.data, memberEntityIds: gd.memberEntityIds.filter((mid: string) => !toRemove.has(mid)) } };
      } else {
        restGroups[id] = e;
      }
    }
    return {
      nodes: split.nodes,
      connectors: split.connectors,
      groups: restGroups,
    };
  }
}
